import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, Icons, iconSize } from '@munifocus/ui';
import { AppShell } from '../components/AppShell';
import { PageIntro } from '../components/PageIntro';
import { ChatPanel } from '../components/chat/ChatPanel';
import { NewConversationDialog } from '../components/chat/NewConversationDialog';
import { useRealtime } from '../lib/realtime';

/**
 * The Messages page.
 *
 * `?conversation=<id>` opens straight into a thread, which is how a link from a
 * file or a notification lands somewhere useful rather than on an empty pane.
 */
export function Messages() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dialogOpen, setDialogOpen] = useState(false);

  const conversationId = searchParams.get('conversation');
  const { enableDesktopNotifications } = useRealtime();

  // Asked here rather than on sign-in: browsers refuse a permission request that
  // is not tied to a user gesture, and a prompt thrown at someone who has just
  // logged in earns a permanent block that no amount of script can undo. Opening
  // Messages is the gesture. No-ops once answered either way.
  useEffect(() => {
    void enableDesktopNotifications();
  }, [enableDesktopNotifications]);

  return (
    <AppShell
      fillViewport
      title={t('common.chat.title')}
      actions={
        <Button
          variant="contained"
          startIcon={<Icons.Plus size={iconSize.md} aria-hidden />}
          onClick={() => setDialogOpen(true)}
        >
          {t('common.chat.newConversation')}
        </Button>
      }
    >
      <PageIntro>{t('common.chat.pageDescription')}</PageIntro>

      {/* Claims the height left over under the intro, rather than naming it.
          `100%` genuinely does not work here — the shell sets `minHeight`, not
          `height`, so the content area has no definite height for a percentage
          to resolve against and the transcript would collapse to nothing. But
          the `calc(100vh - 15rem)` that replaced it was a guess, and measurably
          the wrong one: it left a ~70px band of dead page under the panel, and
          the figure only held while the intro wrapped to exactly two lines —
          a narrower window pushes it to three and the panel overflows.

          `flex: 1` against the shell's `fillViewport` column asks the browser
          for the same thing without the arithmetic, at any width.

          The floor does double duty. It keeps the panel usable on a short laptop
          screen, and being an explicit `min-height` it also overrides the
          `auto` a flex item otherwise gets — which would refuse to shrink below
          the transcript's own content and scroll the page instead of the
          thread. Below the floor the page scrolls, which is the right trade on a
          screen too short to hold a conversation at all. */}
      <Box sx={{ flex: 1, minHeight: 420 }}>
        <ChatPanel initialConversationId={conversationId} fullHeight />
      </Box>

      <NewConversationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onOpened={(id) => {
          setDialogOpen(false);
          // Through the URL rather than local state, so the newly opened thread
          // survives a reload and can be linked to.
          setSearchParams({ conversation: id });
        }}
      />
    </AppShell>
  );
}
